<template>
  <div class="invoice-bundles-table">
    <DataTable
      v-model:filters="filters"
      v-model:expandedRows="expandedRows"
      :value="bundles"
      dataKey="id"
      filterDisplay="row"
      class="border rounded"
      :scrollable="bundles.length > 20"
      :scrollHeight="bundles.length > 20 ? '600px' : undefined"
      sortField="created_at"
      :sortOrder="-1"
    >
      <template #empty>
        <div class="text-center py-4 text-muted">No invoice bundles</div>
      </template>

      <!-- Expand toggle -->
      <Column expander :headerStyle="{ width: '3rem' }" />

      <!-- Status -->
      <Column
        field="status"
        header="Status"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        :headerStyle="{ width: '10rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          <span :class="statusBadgeClass(data.status)">{{
            statusLabel(data.status)
          }}</span>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect
            v-model="filterModel.value"
            :options="statusOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Filter"
            class="p-column-filter w-100"
            display="chip"
            appendTo="body"
            :focusOnHover="false"
            @change="filterCallback()"
          >
            <template #value="{ value, placeholder }">
              <span v-if="value && value.length" class="text-primary fw-bold">
                Active
              </span>
              <span v-else>{{ placeholder }}</span>
            </template>
          </MultiSelect>
        </template>
      </Column>

      <!-- Date -->
      <Column
        field="created_at"
        header="Date"
        sortable
        :headerStyle="{ width: '8rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          {{ formatDateTimeIntoDate(data.created_at) }}
        </template>
      </Column>

      <!-- Fee Invoice Numbers -->
      <Column header="Fee Invoices">
        <template #body="{ data }">
          <span
            v-for="(inv, idx) in data.fee_invoices"
            :key="inv.id"
            class="me-1"
          >
            {{ inv.invoice_number
            }}<template v-if="Number(idx) < data.fee_invoices.length - 1"
              >,</template
            >
          </span>
          <span v-if="data.fee_invoices.length === 0" class="text-muted"
            >-</span
          >
        </template>
      </Column>

      <!-- Expense Invoice Number -->
      <Column header="Expense Invoice">
        <template #body="{ data }">
          <span v-if="data.expense_invoice">{{
            data.expense_invoice.invoice_number
          }}</span>
          <span v-else class="text-muted">-</span>
          <span
            v-if="data.expense_invoice?.is_settlement_record"
            class="badge bg-secondary ms-1"
            >settlement</span
          >
        </template>
      </Column>

      <!-- Items -->
      <Column header="Items" :headerStyle="{ width: '5rem' }">
        <template #body="{ data }">
          {{ data.items.length }}
        </template>
      </Column>

      <!-- Total Amount -->
      <Column
        field="settlement.net_payout"
        header="Total"
        sortable
        :headerStyle="{ width: '8rem' }"
      >
        <template #sorticon="{ sortOrder }">
          <ColumnSortIcon :sortOrder />
        </template>
        <template #body="{ data }">
          {{
            formatMoneyWithCurrency(
              data.settlement.net_payout,
              data.settlement.currency
            )
          }}
        </template>
      </Column>

      <!-- Expanded row detail -->
      <template #expansion="{ data }">
        <div class="p-3">
          <!-- Fee invoices -->
          <div v-if="data.fee_invoices.length > 0" class="mb-3">
            <h6 class="fw-bold mb-2">Fee Invoices</h6>
            <InvoiceDisplayComponent
              v-for="inv in data.fee_invoices"
              :key="inv.id"
              :invoice="inv"
              :is_admin="true"
            />
          </div>

          <!-- Expense invoice -->
          <div v-if="data.expense_invoice" class="mb-3">
            <h6 class="fw-bold mb-2">Expense Invoice</h6>
            <InvoiceDisplayComponent
              :invoice="data.expense_invoice"
              :is_admin="true"
            />
          </div>

          <!-- Items -->
          <div>
            <h6 class="fw-bold mb-2">Items ({{ data.items.length }})</h6>
            <table class="table table-sm table-bordered mb-0">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Description</th>
                  <th class="text-end">Qty</th>
                  <th class="text-end">Unit Price</th>
                  <th class="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in data.items" :key="item.id">
                  <td>{{ formatItemType(item.item_type) }}</td>
                  <td>{{ item.description }}</td>
                  <td class="text-end">{{ item.amount }}</td>
                  <td class="text-end">
                    {{ item.display_unit_price }}
                    {{ currencyLabel(item.currency) }}
                  </td>
                  <td class="text-end">
                    {{
                      formatMoneyWithCurrency(
                        fromUnits(item.gross_units),
                        item.currency
                      )
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Settlement breakdown -->
          <div v-if="data.settlement" class="mt-3">
            <h6 class="fw-bold mb-2">Settlement</h6>
            <table
              class="table table-sm table-bordered mb-0"
              style="max-width: 400px"
            >
              <tbody>
                <tr v-if="data.settlement.go_sales_total > 0">
                  <td>GO sales</td>
                  <td class="text-end">
                    {{
                      formatMoneyWithCurrency(
                        data.settlement.go_sales_total,
                        data.settlement.currency
                      )
                    }}
                  </td>
                </tr>
                <tr
                  v-for="(amount, feeType) in data.settlement.fee_breakdown"
                  :key="feeType"
                >
                  <td class="text-muted">
                    − {{ formatItemType(String(feeType)) }}
                  </td>
                  <td class="text-end text-muted">
                    {{
                      formatMoneyWithCurrency(amount, data.settlement.currency)
                    }}
                  </td>
                </tr>
                <tr v-if="data.settlement.rounding !== 0">
                  <td class="text-muted">Rounding</td>
                  <td class="text-end text-muted">
                    {{
                      formatMoneyWithCurrency(
                        data.settlement.rounding,
                        data.settlement.currency
                      )
                    }}
                  </td>
                </tr>
                <tr
                  v-if="
                    data.settlement.total_fees > 0 ||
                    data.settlement.rounding !== 0
                  "
                  class="fw-bold border-top"
                >
                  <td>Net payout</td>
                  <td class="text-end">
                    {{
                      formatMoneyWithCurrency(
                        data.settlement.net_payout,
                        data.settlement.currency
                      )
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </DataTable>
  </div>
</template>

<script setup lang="ts">
import { FilterMatchMode } from "@primevue/core/api";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import MultiSelect from "primevue/multiselect";
import { ref } from "vue";

import {
  currencyLabel,
  formatDateTimeIntoDate,
  formatMoneyWithCurrency,
} from "@/composables/formatUtils";
import { fromUnits } from "@/composables/money/moneyUtils";
import InvoiceDisplayComponent from "@/money/invoice/InvoiceDisplayComponent.vue";
import type { InvoiceBundle } from "./types";
import { formatItemType } from "./utils";
import ColumnSortIcon from "@/components/common/ColumnSortIcon.vue";

defineProps<{
  bundles: InvoiceBundle[];
}>();

const expandedRows = ref<InvoiceBundle[]>([]);

const filters = ref({
  status: { value: null as string[] | null, matchMode: FilterMatchMode.IN },
});

const statusOptions = [
  { label: "Settled", value: "settled" },
  { label: "Pending Payment", value: "pending_payment" },
  { label: "Pending Settlement", value: "pending_settlement" },
  { label: "Canceled", value: "canceled" },
];

const statusLabel = (status: string): string => {
  const map: Record<string, string> = {
    settled: "Settled",
    pending_payment: "Pending Payment",
    pending_settlement: "Pending Settlement",
    canceled: "Canceled",
  };
  return map[status] ?? status;
};

const statusBadgeClass = (status: string): string => {
  const map: Record<string, string> = {
    settled: "badge bg-success",
    pending_payment: "badge bg-warning text-dark",
    pending_settlement: "badge bg-info text-dark",
    canceled: "badge bg-danger",
  };
  return map[status] ?? "badge bg-secondary";
};
</script>
